import { FaireDate } from "./FaireDate";
import { FaireDateRange } from "./FaireDateRange";
import { AllowedFormats } from "./localization/date-fns/intlDate";

/** Expected ship date range abstraction suitable for storing server-side. */
interface IExpectedShipDateRange {
  /** Timestamp of YYYY-MM-DD 12:59:59.000 UTC to be stored at server. */
  expected_ship_date?: number;
  /**
   * Number of days in the range: 0 or undefined is considered as a single date
   * range.
   */
  expected_ship_window_days?: number;
}

/** Creates {@link FaireDateRange} for expected ship date(s). */
export const faireDateRangeFromExpectedShipDateRange = (
  expectedShipDateRange: IExpectedShipDateRange
): FaireDateRange | undefined => {
  if (!expectedShipDateRange.expected_ship_date) {
    return;
  }
  const startDate = getStartDate(expectedShipDateRange.expected_ship_date);
  if (!startDate) {
    return;
  }
  const endDate = getEndDate(
    startDate,
    expectedShipDateRange.expected_ship_window_days
  );
  return new FaireDateRange(startDate, endDate);
};

export const expectedShipDateRangeFromFaireDateRange = (
  faireDateRange: FaireDateRange
): IExpectedShipDateRange => ({
  expected_ship_date: faireDateRange.startDate.normalizedUtcTimestamp,
  // Number of days: Number of millis between two dates divided by number of
  // millis in a day.
  expected_ship_window_days: Math.round(
    (faireDateRange.endDate.normalizedUtcTimestamp -
      faireDateRange.startDate.normalizedUtcTimestamp) /
      (1000 * 60 * 60 * 24)
  ),
});

/**
 * Formats expected ship date(s).
 *
 * This is just a shortcut for calling {@link faireDateRangeFromExpectedShipDateRange}
 * followed by {@link FaireDateRange#format}.
 */
export const formatExpectedShipDateRange = (
  expectedShipDateRange: IExpectedShipDateRange,
  formatString: AllowedFormats = "MMMM d",
  formatForMultidayString: AllowedFormats = formatString
): string | undefined => {
  const faireDateRange = faireDateRangeFromExpectedShipDateRange(
    expectedShipDateRange
  );
  return faireDateRange
    ? faireDateRange.format({
        dateFormat: formatString,
        multiDayFormat: formatForMultidayString,
      })
    : undefined;
};

const getStartDate = (starDateUtcMillis: number): FaireDate | undefined =>
  FaireDate.fromNormalizedOrLocalEndOfDayTimestamp(starDateUtcMillis);

const getEndDate = (startDate: FaireDate, days?: number): FaireDate => {
  if (!days) {
    return startDate;
  }
  const localEndDate = new Date(startDate.localDateObject);
  localEndDate.setDate(localEndDate.getDate() + days);
  return FaireDate.fromLocalDateObject(localEndDate);
};
